import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Button 
          variant="outline" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          ← Back
        </Button>
        
        <div className="bg-gray-800 rounded-lg p-6 md:p-8 text-gray-200">
          <h1 className="text-3xl font-bold text-white mb-2">Terms of Use</h1>
          <p className="text-gray-400 mb-6">Effective Date: 8 February 2026</p>
          
          <p className="mb-4">
            Website/App: RydrLink ("we", "us", "our")<br/>
            Contact: info@rydrlinkapp.com
          </p>
          
          <p className="mb-6 text-gray-300">
            By using RydrLink, you agree to these Terms of Use. If you do not agree, do not use the platform.
          </p>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">1. About RydrLink</h2>
            <p className="text-gray-300">
              RydrLink is a platform designed to help cyclists connect with other cyclists, organise rides, join group rides, message other users, and share cycling-related information.
            </p>
            <p className="text-gray-300 mt-2">
              RydrLink is currently in beta, meaning features may change, be incomplete, or experience downtime.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">2. Eligibility</h2>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>You must be at least 18 years old to use RydrLink.</li>
              <li>By creating an account, you confirm that the information you provide is accurate and that you are legally able to enter into these Terms.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">3. User Accounts</h2>
            <p className="text-gray-300 mb-2">You are responsible for maintaining the security of your account and login details.</p>
            <p className="text-gray-300 mb-2">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>impersonate another person</li>
              <li>create fake accounts</li>
              <li>use the platform for harassment or abusive behaviour</li>
              <li>use the platform for illegal purposes</li>
            </ul>
            <p className="text-gray-300 mt-2">We reserve the right to suspend or remove any account that breaches these Terms.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">4. Rides, Meetups and Safety Disclaimer</h2>
            <p className="text-gray-300 mb-2">RydrLink helps users connect, but we do not organise rides ourselves.</p>
            <p className="text-gray-300 mb-2">All rides and meetups are organised independently by users. You participate at your own risk.</p>
            <p className="text-gray-300 mb-2">RydrLink is not responsible for:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>accidents, injuries, or death</li>
              <li>theft, loss, or damage to property</li>
              <li>disputes between riders</li>
              <li>unsafe riding behaviour</li>
              <li>route hazards, road conditions, traffic, or weather</li>
            </ul>
            <p className="text-gray-300 mt-2 font-medium">You agree that you are solely responsible for your own safety and actions while using the platform or attending any ride arranged through it.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">5. Conduct and Community Rules</h2>
            <p className="text-gray-300 mb-2">You agree to behave respectfully and responsibly.</p>
            <p className="text-gray-300 mb-2">You must not:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>harass, threaten, abuse, or discriminate against others</li>
              <li>share offensive, hateful, or explicit content</li>
              <li>post personal information of other users without consent</li>
              <li>encourage dangerous cycling behaviour</li>
              <li>spam or advertise without permission</li>
            </ul>
            <p className="text-gray-300 mt-2">We may remove content or suspend accounts at our discretion to protect the community.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">6. Ratings and Reviews</h2>
            <p className="text-gray-300 mb-2">RydrLink may allow users to rate and review other riders.</p>
            <p className="text-gray-300 mb-2">You agree that ratings must be honest and fair. Abuse of the rating system may result in account suspension.</p>
            <p className="text-gray-300">We do not guarantee the accuracy of ratings and are not responsible for any decisions made based on ratings.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">7. Paid Rides and Payments (Future Feature)</h2>
            <p className="text-gray-300 mb-2">Some rides may later allow paid tickets or paid entry.</p>
            <p className="text-gray-300 mb-2">Where payment features are enabled:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>payment processing may be handled by third-party providers (such as Stripe)</li>
              <li>RydrLink may charge a platform fee or commission</li>
              <li>refunds may be subject to organiser rules and payment provider policies</li>
            </ul>
            <p className="text-gray-300 mt-2">Full payment terms may be updated as the platform evolves.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">8. Intellectual Property</h2>
            <p className="text-gray-300 mb-2">All branding, logos, designs, and platform content (excluding user content) are owned by RydrLink.</p>
            <p className="text-gray-300">You may not copy, modify, distribute, or resell any part of the platform without written permission.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">9. User Content</h2>
            <p className="text-gray-300 mb-2">You are responsible for any content you upload, post, or share.</p>
            <p className="text-gray-300 mb-2">By submitting content to RydrLink, you grant us a licence to display and distribute it within the platform for its intended purpose.</p>
            <p className="text-gray-300">We reserve the right to remove content that breaches these Terms.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">10. Beta Service Disclaimer</h2>
            <p className="text-gray-300 mb-2">RydrLink is provided "as is" and "as available".</p>
            <p className="text-gray-300 mb-2">We do not guarantee:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>uninterrupted access</li>
              <li>error-free performance</li>
              <li>that features will always remain available</li>
              <li>that all data will always be retained</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">11. Limitation of Liability</h2>
            <p className="text-gray-300 mb-2">To the maximum extent permitted by law, RydrLink will not be liable for any direct or indirect damages resulting from:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>use of the platform</li>
              <li>user behaviour</li>
              <li>ride participation</li>
              <li>accidents or injuries</li>
              <li>disputes between users</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">12. Termination</h2>
            <p className="text-gray-300 mb-2">You may stop using RydrLink at any time.</p>
            <p className="text-gray-300">We may suspend or terminate your account if you breach these Terms or if we believe your behaviour creates risk to others.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">13. Changes to Terms</h2>
            <p className="text-gray-300 mb-2">We may update these Terms at any time.</p>
            <p className="text-gray-300 mb-2">If changes are significant, we will update the effective date and may notify users.</p>
            <p className="text-gray-300">Continued use of RydrLink means you accept the updated Terms.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">14. Governing Law</h2>
            <p className="text-gray-300 mb-2">These Terms are governed by the laws of England and Wales.</p>
            <p className="text-gray-300">Any disputes will be handled in the courts of England and Wales.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
